import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlanEdge, PlanNode } from '../types';
import type { CanvasStoreBundle } from './canvasStore';
import { prefersReducedMotion } from './events';
import type { Box, Viewport, XY } from './geometry';
import { tidyLayout } from './layout';
import type { ViewportApi } from './useViewport';

const ANIMATE_MS = 200;
/** How long the Keep or Undo bar waits before keeping on its own. */
export const TIDY_WINDOW_MS = 20000;

interface Options {
  /** Today's nodes and arrows, read when Tidy up runs. */
  graph: () => { nodes: PlanNode[]; edges: PlanEdge[] };
  /** Save these nodes where they now stand. */
  onKeep: (ids: string[]) => void;
  /** Tidy up found every card already in place. */
  onAlreadyTidy: () => void;
}

export interface TidyApi {
  /** The Keep or Undo bar is up. */
  pending: boolean;
  start: () => void;
  keep: () => void;
  undo: () => void;
}

interface Pending {
  before: Record<string, XY>;
  after: Record<string, XY>;
  moved: string[];
  viewport: Viewport;
  timer: number;
}

export function useTidy(bundle: CanvasStoreBundle, viewport: ViewportApi, opts: Options): TidyApi {
  const { store } = bundle;
  const latest = useRef(opts);
  latest.current = opts;
  const [pending, setPending] = useState(false);
  const state = useRef<Pending | null>(null);
  const anim = useRef<{ frame: number; to: Record<string, XY>; ids: string[] } | null>(null);

  /** Stop a glide where it was headed. */
  const finish = useCallback(() => {
    const a = anim.current;
    if (!a) return;
    cancelAnimationFrame(a.frame);
    anim.current = null;
    store.getState().moveNodes(a.to);
    bundle.endMove(a.ids);
  }, [store, bundle]);

  /** Move nodes to `to` over 200ms, edges following every frame. Reduced motion jumps. */
  const glide = useCallback(
    (to: Record<string, XY>) => {
      finish();
      const ids = Object.keys(to);
      if (prefersReducedMotion()) {
        store.getState().moveNodes(to);
        return;
      }
      const boxes = store.getState().nodes;
      const from: Record<string, XY> = {};
      ids.forEach((id) => {
        if (boxes[id]) from[id] = { x: boxes[id].x, y: boxes[id].y };
      });
      // A reload landing mid-glide must not yank a card back to its old saved spot.
      bundle.beginMove(ids);
      // Timed from the first frame's own clock: a rAF timestamp need not share performance.now's origin.
      let t0: number | null = null;
      const step = (now: number) => {
        const a = anim.current;
        if (!a) return;
        if (t0 === null) t0 = now;
        const t = Math.min(1, (now - t0) / ANIMATE_MS);
        if (t >= 1) {
          finish();
          return;
        }
        const e = 1 - (1 - t) ** 3;
        const frame: Record<string, XY> = {};
        Object.keys(from).forEach((id) => {
          frame[id] = { x: from[id].x + (to[id].x - from[id].x) * e, y: from[id].y + (to[id].y - from[id].y) * e };
        });
        store.getState().moveNodes(frame);
        a.frame = requestAnimationFrame(step);
      };
      anim.current = { frame: requestAnimationFrame(step), to, ids };
    },
    [store, bundle, finish],
  );

  const keep = useCallback(() => {
    const p = state.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    state.current = null;
    finish();
    setPending(false);
    latest.current.onKeep(p.moved);
  }, [finish]);

  const undo = useCallback(() => {
    const p = state.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    state.current = null;
    const back: Record<string, XY> = {};
    p.moved.forEach((id) => {
      if (p.before[id]) back[id] = p.before[id];
    });
    glide(back);
    viewport.goTo(p.viewport, true);
    setPending(false);
  }, [glide, viewport]);

  const start = useCallback(() => {
    keep();
    const boxes = store.getState().nodes;
    const { nodes, edges } = latest.current.graph();
    const live = nodes.filter((n) => boxes[n.id]);
    if (live.length === 0) return;

    const sizes: Record<string, { width: number; height: number } | undefined> = {};
    live.forEach((n) => {
      const b = boxes[n.id];
      sizes[n.id] = b.measured ? { width: b.width, height: b.height } : undefined;
    });
    const laid = tidyLayout(live, edges, sizes);
    // The drawing keeps the top-left corner the cards already had, so the view moves least.
    const ox = live.reduce((m, n) => Math.min(m, boxes[n.id].x), Infinity);
    const oy = live.reduce((m, n) => Math.min(m, boxes[n.id].y), Infinity);

    const before: Record<string, XY> = {};
    const after: Record<string, XY> = {};
    const moved: string[] = [];
    const targets: Box[] = [];
    live.forEach((n) => {
      const b = boxes[n.id];
      const p = laid.get(n.id);
      if (!p) return;
      const to = { x: Math.round(ox + p.x), y: Math.round(oy + p.y) };
      before[n.id] = { x: b.x, y: b.y };
      after[n.id] = to;
      targets.push({ ...to, width: b.width, height: b.height });
      if (to.x !== b.x || to.y !== b.y) moved.push(n.id);
    });

    if (moved.length === 0) {
      viewport.fitView(true);
      latest.current.onAlreadyTidy();
      return;
    }
    const vp0 = { ...viewport.vp.current };
    glide(after);
    viewport.fitView(true, targets);
    state.current = { before, after, moved, viewport: vp0, timer: window.setTimeout(keep, TIDY_WINDOW_MS) };
    setPending(true);
  }, [store, keep, glide, viewport]);

  // Leaving the canvas with the bar up keeps the layout, as waiting it out would.
  useEffect(
    () => () => {
      const a = anim.current;
      if (a) {
        cancelAnimationFrame(a.frame);
        bundle.endMove(a.ids);
      }
      anim.current = null;
      const p = state.current;
      if (!p) return;
      window.clearTimeout(p.timer);
      state.current = null;
      store.getState().moveNodes(p.after);
      latest.current.onKeep(p.moved);
    },
    [store, bundle],
  );

  return useMemo(() => ({ pending, start, keep, undo }), [pending, start, keep, undo]);
}
